import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import DeleteIcon from "../../../../../assets/images/icons/DeleteIcon";
import DownloadIcon from "../../../../../assets/images/icons/DownloadIcon";
import FullscreenIcon from "../../../../../assets/images/icons/Image/FullscreenIcon";
import BriaTooltip from "../../../../../components/common/BriaTooltip/BriaTooltip";
import CopyToClipboardButton from "../../../../../components/common/CopyToClipboardButton/CopyToClipboardButton";
import { BriaImageProps, ImageErrorType } from "../../../../../components/common/Galleries/BriaImage";
import LoadingAnimation from "../../../../../components/common/LoadingAnimation/LoadingAnimation";
import RouterConstants from "../../../../../constants/RouterConstants.ts";
import { useAppStore } from "../../../../../hooks/useStores";
import { PlaygroundImage, PlaygroundResult } from "../../../../../models/image-to-image";
import { TextToImageConfigType } from "../../../../../models/text-to-image";
import iframeStore from "../../../../IframeNew/iframe-store.tsx";
import PlaygroundImageOverlay from "../../../../Playground/Images/Overlay/PlaygroundImageOverlay.tsx";
import TextToImageGallery from "../../../../Playground/TextToImage/Generator/Gallery/TextToImageGallery.tsx";
import { shouldShowUserPromptInGallery } from "../../../utils/tg-playground-helpers";
import { generateEvaluationConfigFile, generateStructuredPromptJsonFile } from "../../../utils/tg-utils.ts";
import styles from "./TgImageGenerationResult.module.scss";

type Props = {
	playgroundResult: PlaygroundResult;
	showFeedbackTutorialOverlay?: boolean;
};

const AVG_RESULT_DURATION = 40;

const TgImageGenerationResult = ({ playgroundResult, showFeedbackTutorialOverlay = false }: Props) => {
	const { t } = useTranslation("tg", { keyPrefix: "app.image.result" });
	const { t: modelTrans } = useTranslation("translation", { keyPrefix: "staticDropdowns.genModels" });
	const config = playgroundResult.config as TextToImageConfigType;
	const { playgroundStore, uiStore } = useAppStore();
	const [openFullScreen, setOpenFullScreen] = useState(false);
	const loading = !!playgroundResult.images.filter((image) => image.loading).length;
	const loadingDuration = AVG_RESULT_DURATION * 1.1;

	const isTgEvaluation = location.pathname.includes(RouterConstants.NEW_TG.MODELS.EVALUATION.path);
	const isFibo = config.training_version === "fibo";

	const defaultDownloadFileName = `tailored_generation_${config.tailored_model_id}_${new Date()
		.toISOString()
		.replace("T", "_")
		.replace(/:/g, "-")
		.substring(0, 19)}`;

	const handleDownloadImages = () => {
		const structured_prompt = playgroundResult.images?.find(
			(img: PlaygroundImage) => img?.structured_prompt,
		)?.structured_prompt;

		const additionalFiles: File[] = [
			...(isTgEvaluation ? [generateEvaluationConfigFile(config)] : []),
			...(structured_prompt
				? [
						generateStructuredPromptJsonFile(
							structured_prompt,
							`tailored_generation_${config.tailored_model_id}.json`,
						),
					]
				: []),
		];
		uiStore.showExportImagePopup({
			downloadProps: {
				fileName: defaultDownloadFileName,
			},
			images: playgroundResult.images.filter((image) => !image.error),
			additionalFiles: additionalFiles.length > 0 ? additionalFiles : undefined,
		});
	};

	const formattedVersion = config.model_version
		? config.model_version.charAt(0).toUpperCase() + config.model_version.slice(1).toLowerCase()
		: "";

	return (
		<Box className={clsx(styles.ttiGalleryWrapper, "galleryWrapper")}>
			<Box className={styles.header}>
				<Box className={styles.topHeader}>
					{config.is_refine ? (
						<Typography className={styles.labelModelName}>
							{config.is_refine
								? config.model_name
									? modelTrans("tg_refiner", { name: config.model_name })
									: modelTrans("bria_fibo_refiner", { version: formattedVersion })
								: (config.model_name ?? modelTrans("bria2_3_fast"))}
						</Typography>
					) : (
						<Typography className={styles.labelModelName}>
							{config.model_name ?? modelTrans("bria2_3_fast")}
							{isTgEvaluation && (
								<Box className={styles.badgesContainer}>
									<Box component="span" className={styles.badge}>
										{config.training_version}
									</Box>
									{config.checkpoint_number !== "default" && (
										<Box component="span" className={styles.badge}>
											{config.checkpoint_number}
										</Box>
									)}
								</Box>
							)}
						</Typography>
					)}
					{loading ? (
						<LoadingAnimation loading={loading} progressBarTime={loadingDuration} showAnimation={false} />
					) : (
						<Box className={styles.actionBtns}>
							<CopyToClipboardButton
								className={styles.iconButton}
								textToCopy={
									config.is_refine && config.refine_prompt
										? config.refine_prompt
										: shouldShowUserPromptInGallery(window.location.pathname) || isFibo
											? config?.prompt
											: config?.generation_prefix
												? `${config?.generation_prefix}${config?.prompt}`
												: config?.prompt
								}
								tooltipText={t("copyPrompt")}
							/>
							{(!iframeStore.isIframe() ||
								iframeStore.iframe.config.general_config?.enable_download ||
								iframeStore.iframe.config.general_config?.enable_save) && (
								<BriaTooltip title={t("download")}>
									<DownloadIcon className={styles.iconButton} onClick={handleDownloadImages} />
								</BriaTooltip>
							)}
							<BriaTooltip title={t("delete")}>
								<DeleteIcon
									className={styles.iconButton}
									onClick={() => playgroundStore.deleteResult(playgroundResult.id)}
								/>
							</BriaTooltip>
							<BriaTooltip title={t("fullScreen")}>
								<FullscreenIcon className={styles.iconButton} onClick={() => setOpenFullScreen(true)} />
							</BriaTooltip>
						</Box>
					)}
				</Box>
				<Box className={styles.headerText}>
					{config.is_refine && config.refine_prompt ? (
						config.refine_prompt
					) : shouldShowUserPromptInGallery(window.location.pathname) || isFibo ? (
						config?.prompt
					) : config?.generation_prefix && !config.restyle_portrait ? (
						<>
							<span className={styles.generationPrefix}>{config?.generation_prefix}</span>
							{config?.prompt}
						</>
					) : (
						config?.prompt
					)}
				</Box>
			</Box>
			<TextToImageGallery
				images={playgroundResult.images.map(
					(image, imageIndex) =>
						({
							ImageOverlay: (
								<PlaygroundImageOverlay
									{...{ image }}
									hideAiEditorButton={isTgEvaluation}
									highlightFeedbackButtons={imageIndex === 0 && showFeedbackTutorialOverlay}
								/>
							),
							showConstantOverlay: imageIndex === 0 && showFeedbackTutorialOverlay,
							displayOverlay: "customHover",
							aspectRatio: image?.aspect_ratio,
							selectable: true,
							htmlJsonData: image.id,
							async: loading,
							onSuccessPulling: () => playgroundStore.onSuccessResult(image),
							onErrorPulling: (errorType: ImageErrorType) =>
								playgroundStore.onErrorResult(image, errorType),
							fullScreenProps: {
								fileName: playgroundStore.getFileName(image),
								format: (image.config as TextToImageConfigType)?.format,
							},
							...image,
						}) as BriaImageProps & PlaygroundImage,
				)}
				fullScreenProps={{
					open: openFullScreen,
					onClose: () => setOpenFullScreen(false),
					displayMode: "scroll",
				}}
				className={styles.gallery}
			/>
		</Box>
	);
};

export default observer(TgImageGenerationResult);
